// src/pages/appointment-calendar/components/AppointmentPaymentInfo.jsx
import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/auth/AuthProvider";
import {
  CreditCard,
  CheckCircle,
  AlertTriangle,
  XCircle,
  RotateCcw,
  Loader2,
  Link,
  Copy,
  Check,
} from "lucide-react";

export default function AppointmentPaymentInfo({ appointment }) {
  const { currentBusinessId, permissions } = useAuth();
  const [refundInfo, setRefundInfo] = useState(null);
  const [loadingRefund, setLoadingRefund] = useState(false);
  const [refunding, setRefunding] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [paymentLink, setPaymentLink] = useState("");
  const [generatingLink, setGeneratingLink] = useState(false);
  const [copied, setCopied] = useState(false);

  const isOwnerOrDirector =
    permissions?.isSuperAdmin ||
    permissions?.isOwnerCurrentBusiness ||
    permissions?.isDirectorCurrentBusiness;

  const handleGenerateLink = async () => {
    setGeneratingLink(true);
    setError("");
    try {
      const { data } = await api.post(
        `/b/appointments/${appointment.id}/payment-link`,
        {},
        { headers: { "X-Business-Id": currentBusinessId } }
      );
      setPaymentLink(data.checkout_url);
    } catch (e) {
      const detail =
        e?.response?.data?.stripe?.[0] ||
        e?.response?.data?.detail ||
        e?.message ||
        "Nu am putut genera link-ul de plată.";
      setError(detail);
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(paymentLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Check if appointment has payment
  if (!appointment?.service?.requires_payment) {
    return null; // No payment required for this service
  }

  const paymentStatus = appointment?.payment_status || "not_required";
  const amountPaid = parseFloat(appointment?.amount_paid || 0);
  const amountRefunded = parseFloat(appointment?.amount_refunded || 0);
  const amountTotal = parseFloat(appointment?.amount_total || 0);

  const canRefund =
    isOwnerOrDirector &&
    (paymentStatus === "paid" || paymentStatus === "partially_refunded") &&
    amountPaid > amountRefunded;

  useEffect(() => {
    if (canRefund && !refundInfo) {
      // Load refund calculation
      const loadRefundInfo = async () => {
        setLoadingRefund(true);
        try {
          const { data } = await api.post(
            `/b/appointments/${appointment.id}/calculate-refund`,
            {},
            {
              headers: { "X-Business-Id": currentBusinessId },
            }
          );
          setRefundInfo(data);
        } catch (e) {
          console.error("Failed to load refund info:", e);
        } finally {
          setLoadingRefund(false);
        }
      };
      loadRefundInfo();
    }
  }, [canRefund, appointment.id, currentBusinessId, refundInfo]);

  const handleRefund = async () => {
    if (
      !confirm(
        `Confirmi refund de ${refundInfo?.refund_amount} RON pentru această programare?\n\nMotiv: ${refundInfo?.reason}`
      )
    ) {
      return;
    }

    setRefunding(true);
    setError("");
    setSuccess("");

    try {
      const { data } = await api.post(
        `/b/appointments/${appointment.id}/refund`,
        {
          reason: refundInfo?.reason || "Cerere client",
        },
        {
          headers: { "X-Business-Id": currentBusinessId },
        }
      );

      setSuccess(`Refund procesat cu succes: ${data.refund_amount} RON`);

      // Refresh page after 2 seconds
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (e) {
      const detail =
        e?.response?.data?.detail ||
        e?.response?.data?.stripe?.[0] ||
        e?.message ||
        "Eroare la procesare refund.";
      setError(detail);
    } finally {
      setRefunding(false);
    }
  };

  const StatusIcon = ({ status }) => {
    switch (status) {
      case "paid":
        return <CheckCircle className="text-green-600" />;
      case "processing":
        return <Loader2 className="text-blue-600 animate-spin" />;
      case "failed":
        return <XCircle className="text-red-600" />;
      case "refunded":
      case "partially_refunded":
        return <RotateCcw className="text-orange-600" />;
      case "pending":
        return <AlertTriangle className="text-yellow-600" />;
      default:
        return <CreditCard className="text-gray-600" />;
    }
  };

  const statusLabels = {
    not_required: "Nu necesită plată",
    pending: "În așteptare",
    processing: "Se procesează",
    paid: "Plătit",
    partially_refunded: "Parțial rambursat",
    refunded: "Rambursat",
    failed: "Eșuat",
    cancelled: "Anulat",
  };

  return (
    <div className="mb-6">
      <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
        <CreditCard />
        Informații Plată
      </h3>

      <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3">
        {/* Status */}
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-muted-foreground">Status plată:</span>
          <div className="flex items-center gap-2">
            <StatusIcon status={paymentStatus} />
            <span className="text-sm font-medium">
              {statusLabels[paymentStatus] || paymentStatus}
            </span>
          </div>
        </div>

        {/* Amount Total */}
        {amountTotal > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-muted-foreground">Sumă totală:</span>
            <span className="text-sm font-medium">{amountTotal.toFixed(2)} RON</span>
          </div>
        )}

        {/* Amount Paid */}
        {amountPaid > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-muted-foreground">Sumă plătită:</span>
            <span className="text-sm font-medium text-green-600">
              {amountPaid.toFixed(2)} RON
            </span>
          </div>
        )}

        {/* Amount Refunded */}
        {amountRefunded > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-muted-foreground">Sumă rambursată:</span>
            <span className="text-sm font-medium text-orange-600">
              {amountRefunded.toFixed(2)} RON
            </span>
          </div>
        )}

        {/* Payment Intent ID */}
        {appointment?.stripe_payment_intent_id && (
          <div className="pt-2 border-t border-border">
            <span className="text-xs text-muted-foreground">
              Stripe Payment Intent:{" "}
              <span className="font-mono">{appointment.stripe_payment_intent_id}</span>
            </span>
          </div>
        )}

        {/* Generate Payment Link */}
        {isOwnerOrDirector && paymentStatus !== "paid" && paymentStatus !== "refunded" && (
          <div className="pt-3 border-t border-border">
            {!paymentLink ? (
              <button
                onClick={handleGenerateLink}
                disabled={generatingLink}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-primary/50 text-primary text-sm font-medium hover:bg-primary/5 disabled:opacity-50"
              >
                {generatingLink ? (
                  <><Loader2 size={15} className="animate-spin" /> Se generează...</>
                ) : (
                  <><Link size={15} /> Trimite link de plată clientului</>
                )}
              </button>
            ) : (
              <div className="space-y-2">
                <div className="text-xs text-muted-foreground font-medium">Link de plată generat:</div>
                <div className="flex items-center gap-2 p-2 rounded bg-muted border border-border text-xs font-mono break-all">
                  <span className="flex-1 truncate">{paymentLink}</span>
                  <button
                    onClick={handleCopy}
                    className="shrink-0 p-1 rounded hover:bg-background text-muted-foreground hover:text-foreground"
                    title="Copiază"
                  >
                    {copied ? <Check size={14} className="text-green-600" /> : <Copy size={14} />}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">Trimite acest link clientului pe WhatsApp sau SMS.</p>
              </div>
            )}
          </div>
        )}

        {/* Error/Success Messages */}
        {error && (
          <div className="mt-3 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-600">
            {error}
          </div>
        )}

        {success && (
          <div className="mt-3 rounded-lg bg-green-50 border border-green-200 p-3 text-sm text-green-600">
            {success}
          </div>
        )}

        {/* Refund Section */}
        {canRefund && (
          <div className="mt-4 pt-4 border-t border-border">
            <div className="mb-3">
              <div className="text-xs font-medium text-muted-foreground mb-1">
                Refund disponibil:
              </div>
              {loadingRefund ? (
                <div className="text-sm text-muted-foreground flex items-center gap-2">
                  <Loader2 className="animate-spin" />
                  Se calculează...
                </div>
              ) : refundInfo ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Sumă refund:</span>
                    <span className="text-lg font-semibold text-orange-600">
                      {refundInfo.refund_amount} RON
                    </span>
                  </div>
                  {refundInfo.refund_percent > 0 && (
                    <div className="text-xs text-muted-foreground">
                      ({refundInfo.refund_percent}% din suma plătită)
                    </div>
                  )}
                  <div className="text-xs text-muted-foreground bg-muted/50 rounded p-2">
                    📋 {refundInfo.reason}
                  </div>

                  {refundInfo.can_refund ? (
                    <button
                      onClick={handleRefund}
                      disabled={refunding}
                      className="w-full mt-2 px-4 py-2 rounded-lg bg-orange-600 text-white font-medium hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {refunding ? (
                        <>
                          <Loader2 className="animate-spin" />
                          Se procesează...
                        </>
                      ) : (
                        <>
                          <RotateCcw />
                          Procesează Refund
                        </>
                      )}
                    </button>
                  ) : (
                    <div className="text-xs text-muted-foreground italic mt-2">
                      Nu se poate face refund conform politicii business-ului.
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
